/**
 * What an entity-participation `entityType` means, in one place.
 *
 * `entityType` is a raw wire constant — `SALES_ORDER`, `PURCHASE_ORDER`,
 * `SHIPMENT` — and the partner panel was interpolating it straight into
 * sentences: "Companies with access to this sales_order", "Add Partner to
 * SALES_ORDER" (QA ledger ORD-004).
 *
 * It was also being matched against by hand in two places, and both were
 * wrong, because the code was written for values nobody sends:
 *
 *  - `ROLE_SUGGESTIONS` was keyed on `ORDER`, so the two order types — the
 *    only order types anything passes — got no role suggestions at all.
 *  - The permission picker asked for access-object categories `ORDERS`,
 *    `SHIPMENTS` and `FINANCE`. The real categories are singular: those three
 *    return ZERO objects. So a SHIPMENT offered an empty permission list, and
 *    a sales order fell through the chain to `INVENTORY` and offered
 *    inventory permissions.
 *
 * One table now answers all three questions, so a new entity type is added
 * once and cannot be half-added.
 */

export interface EntityTypeInfo {
  /** Lower-case noun for inline use: "…access to this sales order". */
  noun: string;
  /** Title-case for headings: "Add Partner to Sales Order". */
  title: string;
  /** `access_objects.category` whose permissions apply to this entity. */
  accessCategory: string;
  /** Partner roles worth offering for this kind of entity. */
  roles: string[];
}

const PARTY_ROLES = ['BUYER', 'SELLER', 'BROKER', 'LOGISTICS_PROVIDER'];

export const ENTITY_TYPES: Record<string, EntityTypeInfo> = {
  SALES_ORDER: {
    noun: 'sales order',
    title: 'Sales Order',
    accessCategory: 'ORDER',
    roles: PARTY_ROLES,
  },
  PURCHASE_ORDER: {
    noun: 'purchase order',
    title: 'Purchase Order',
    accessCategory: 'ORDER',
    roles: PARTY_ROLES,
  },
  ORDER: { noun: 'order', title: 'Order', accessCategory: 'ORDER', roles: PARTY_ROLES },
  SHIPMENT: {
    noun: 'shipment',
    title: 'Shipment',
    accessCategory: 'SHIPMENT',
    roles: ['SHIPPER', 'CONSIGNEE', 'FORWARDER', 'CARRIER', 'CUSTOMS_AGENT'],
  },
  INVOICE: {
    noun: 'invoice',
    title: 'Invoice',
    accessCategory: 'INVOICE',
    roles: ['ISSUER', 'RECIPIENT', 'PAYER', 'PAYMENT_PROCESSOR'],
  },
  WAREHOUSE: {
    noun: 'warehouse',
    title: 'Warehouse',
    accessCategory: 'WAREHOUSE',
    roles: ['OPERATOR', 'CLIENT', 'LOGISTICS_PROVIDER'],
  },
  INVENTORY_ITEM: {
    noun: 'inventory item',
    title: 'Inventory Item',
    accessCategory: 'INVENTORY',
    roles: ['OWNER', 'CUSTODIAN', 'SUPPLIER'],
  },
  // A temporary permit, shared with its whole renewal chain (ADR 0032 in
  // documentation/temporary-permits). A country may require a customs agent on it.
  TEMPO_PERMIT: {
    noun: 'permit',
    title: 'Permit',
    accessCategory: 'TEMPORARY-PERMITS',
    roles: ['CUSTOMS_AGENT', 'FORWARDER', 'END_CLIENT'],
  },
};

/** `SALES_ORDER` -> `sales order`, for a type nobody has described yet. */
const derivedNoun = (entityType: string): string =>
  entityType.replace(/[_-]+/g, ' ').trim().toLowerCase();

/**
 * The noun to drop into a sentence. Falls back to a derived one rather than
 * the raw constant, so an entity type added tomorrow reads as English here
 * even before anyone adds it to the table above.
 */
export const entityTypeNoun = (entityType: string): string =>
  ENTITY_TYPES[entityType]?.noun ?? derivedNoun(entityType);

/** The noun for a heading: "Add Partner to Sales Order". */
export const entityTypeTitle = (entityType: string): string => {
  const known = ENTITY_TYPES[entityType];
  if (known) return known.title;
  const noun = derivedNoun(entityType);
  return noun.replace(/\b[a-z]/g, (c) => c.toUpperCase());
};

/**
 * Access-object category for this entity's permissions.
 *
 * Undefined for an unknown type, which the caller must treat as "do not
 * filter by category" rather than guessing — the guess is what put inventory
 * permissions on a sales order.
 */
export const entityAccessCategory = (entityType: string): string | undefined =>
  ENTITY_TYPES[entityType]?.accessCategory;

export const entityRoleSuggestions = (entityType: string): string[] =>
  ENTITY_TYPES[entityType]?.roles ?? [];
